import { DEBAT, SOURCES } from "../content/textes";
import { stockFor } from "../model/run";
import { useApp } from "../state/store";

/** Le modèle en clair, les hypothèses, les sources, les limites. */
export function Methode() {
  const route = useApp((s) => s.route)!;
  const settings = useApp((s) => s.scenario.settings);
  const h = route.hypotheses;
  const stock = stockFor(settings);
  return (
    <section className="methode">
      <h2>Méthode</h2>
      <p>
        Le temps de parcours est calculé par intégration spatiale du mouvement du train, pas de 25 m, sur la géométrie
        officielle du tracé d'étude ({route.label}, {(route.length_m / 1000).toFixed(1)} km). À chaque pas : effort de
        traction limité par l'adhérence puis par la puissance, résistance à l'avancement (formule de Davis), pente du
        profil rail, plafond de vitesse de la section. Le freinage est remonté depuis chaque arrêt et chaque baisse de
        limitation. À chaque arrêt intermédiaire s'ajoutent le temps d'arrêt et une pénalité de jerk.
      </p>
      <h3>Hypothèses</h3>
      <ul>
        <li>
          Matériel : {stock.label}, {(stock.mass_kg / 1000).toFixed(0)} t, {(stock.power_w / 1e3).toFixed(0)} kW,
          démarrage {stock.a0} m/s², freinage {stock.d} m/s², {(stock.vmax_ms * 3.6).toFixed(0)} km/h max.
        </li>
        <li>Temps d'arrêt en gare : {settings.tarr} s, identique partout. Jerk 0,6 m/s³.</li>
        <li>
          Vitesse de ligne {h.vmax_line_kmh} km/h. Limitation en courbe déduite du rayon de courbure de la polyligne
          (fenêtre de {h.curvature_window_m} m) avec une accélération latérale de {h.a_lat_ms2} m/s² ; sans donnée
          publique sur les vitesses par section, c'est une hypothèse, réglable ci-dessus (« Réglages »).
        </li>
        <li>
          Profil en long : terrain IGN, médiane glissante sur 2 km (les ravines et crêtes de moins d'un kilomètre sont
          franchies à niveau, en viaduc ou en tunnel), puis pente plafonnée à {(h.imax * 100).toFixed(1)} %. Le MNT
          donne le terrain, pas le rail : ce profil est une reconstitution, pas celui du projet. La pente change le
          résultat de moins d'une minute.
        </li>
        <li>Pas de marge de régularité, pas d'arrêt prolongé en gare principale, pas de correspondance.</li>
      </ul>
      <h3>Les graphiques</h3>
      <p>
        <strong>Profil</strong> : altitude du terrain (IGN) et du rail reconstitué, puis vitesse du train en chaque point,
        avec la limitation de section. Chaque creux est un arrêt. <strong>Espace-temps</strong> : temps écoulé depuis
        le départ en fonction de la distance, pour ce scénario, pour le tracé de référence, et pour la voiture entre
        les centres-villes. La voiture est donnée en fourchette : heure creuse d'après OpenStreetMap sans trafic, heure
        de pointe d'après le dossier du maître d'ouvrage, qui annonce « plus de 60 minutes » sur Saint-Benoît →
        Saint-Denis, Saint-Denis → Saint-Paul et Saint-Paul → Saint-Pierre ; sur Saint-Pierre → Saint-Joseph, non
        documenté, on prend l'heure creuse fois 1,5. Ni le rabattement jusqu'à la gare ni la recherche de
        stationnement ne sont comptés, d'un côté comme de l'autre.
      </p>
      <h3>Ce que ça ne dit pas</h3>
      <p>
        Le dossier du maître d'ouvrage annonce environ 135 minutes de bout en bout ; ce modèle en donne moins avec les
        mêmes gares. L'écart tient aux hypothèses ci-dessus, pas à la longueur du tracé, qui a été vérifiée. Il ne faut
        pas lire ce site comme une contre-expertise des temps annoncés : il sert à comparer des scénarios entre eux,
        avec des hypothèses identiques. Une gare de plus coûte environ une minute à ceux qui la traversent, et fait
        gagner dix à vingt minutes de rabattement à ceux qui habitent à côté. Le second terme n'est pas encore calculé
        ici (population desservie : à venir).
      </p>
      <h3>Sources</h3>
      <ul className="sources">
        {SOURCES.map((s) => (
          <li key={s.quoi}>
            <strong>{s.quoi}</strong> — {s.source}, {s.date}.
          </li>
        ))}
      </ul>
      <p>
        <a href={DEBAT.page} target="_blank" rel="noopener">
          Page du débat public
        </a>{" "}
        ·{" "}
        <a href={DEBAT.dmo} target="_blank" rel="noopener">
          Dossier du maître d'ouvrage (PDF)
        </a>{" "}
        ·{" "}
        <a href={DEBAT.plateforme} target="_blank" rel="noopener">
          Plateforme participative
        </a>
      </p>
      <p className="note">
        {route.source}. Outil citoyen indépendant, code source ouvert. Les chiffres se recalculent dans votre navigateur
        : rien n'est envoyé nulle part, le lien de partage contient tout votre scénario.
      </p>
    </section>
  );
}
